import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, ArrowRight, Check, Search, FileCheck, Users, 
  TrendingUp, Target, BarChart3, Calendar, UserCheck,
  ClipboardList, Zap, Clock, DollarSign, ChevronDown,
  Building2, ArrowUpRight, CheckCircle
} from 'lucide-react';

const EmployersPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 100);
  }, []);

  const features = [
    {
      icon: Search,
      title: 'Search Verified Talent',
      desc: 'Find pre-vetted candidates with verified work histories. Filter by tier, skills, industry, and location.',
      highlight: 'Skip the resume screening'
    },
    {
      icon: FileCheck,
      title: 'Create VETT Records',
      desc: 'Document employee performance with structured fields for achievements, skills, ratings, and rehire eligibility.',
      highlight: 'Build your employer brand'
    },
    {
      icon: Users,
      title: 'Candidate Pipeline',
      desc: 'Track candidates through 6 stages: Spotted, Contacted, Interviewing, Offer Made, Hired, Passed.',
      highlight: 'Drag-and-drop Kanban board'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      desc: 'Measure team performance, hiring efficiency, and record completion rates across your organization.',
      highlight: 'Data-driven decisions'
    },
    {
      icon: Calendar,
      title: 'Review Scheduling',
      desc: 'Schedule and track performance reviews with calendar integration. Never miss a review cycle.',
      highlight: 'Automated reminders'
    },
    {
      icon: UserCheck,
      title: 'Reference Requests',
      desc: 'Request and manage references directly through the platform. Structured responses, verified sources.',
      highlight: 'End the phone tag'
    }
  ];

  const benefits = [
    { value: '75%', label: 'reduced verification time', desc: 'Skip manual verification and reference calls' },
    { value: '$14K', label: 'saved per hire', desc: 'Compared to average recruiter fee' },
    { value: '46%', label: 'fewer bad hires', desc: 'Verified records reduce hiring mistakes' },
  ];

  const faqs = [
    { q: 'How does the 14-day trial work?', a: 'Full access to all features for 14 days, no credit card required. After the trial, choose a plan to continue or your access ends. We don\'t do lingering free accounts.' },
    { q: 'Can I import existing employee data?', a: 'Yes. You can create VETT records for current and past employees. They\'ll be invited to acknowledge the records, making them verified and searchable.' },
    { q: 'What happens to records when employees leave?', a: 'Records are permanent and owned by the employee. They become part of the employee\'s verified work history. You can mark the employee as departed.' },
    { q: 'How do VETT tiers help me find talent?', a: 'Filter candidates by verification depth tier: Distinguished (75+ score, comprehensive history), Established (50-74, multiple records), Verified (20-49, initial verification), or Unverified (0-19, in progress). Higher tiers indicate more verified history.' },
    { q: 'Is there an API for ATS integration?', a: 'ATS integrations are in development for the Scale plan. Contact us for early access or custom integration needs.' },
  ];

  const employerPlans = [
    {
      name: 'Starter',
      price: billingCycle === 'annual' ? 249 : 299,
      period: billingCycle === 'annual' ? '/mo (billed annually)' : '/month',
      savings: billingCycle === 'annual' ? 'Get 2 months free' : null,
      description: 'For small teams getting started',
      features: [
        'Up to 20 records',
        '50 talent searches/month',
        'Candidate pipeline',
        'Basic analytics',
        'Email support',
      ],
      addOn: '$25 per 10 additional records',
      planId: 'starter',
      featured: false,
    },
    {
      name: 'Growth',
      price: billingCycle === 'annual' ? 666 : 799,
      period: billingCycle === 'annual' ? '/mo (billed annually)' : '/month',
      savings: billingCycle === 'annual' ? 'Get 2 months free' : null,
      description: 'For growing hiring teams',
      features: [
        'Up to 100 records',
        'Unlimited searches',
        'Full analytics',
        'Review scheduling',
        'PDF export',
        'Reference request tool',
        'Priority support',
      ],
      planId: 'growth',
      featured: true,
    },
    {
      name: 'Scale',
      price: billingCycle === 'annual' ? 1666 : 1999,
      period: billingCycle === 'annual' ? '/mo (billed annually)' : '/month',
      savings: billingCycle === 'annual' ? 'Get 2 months free' : null,
      description: 'For large hiring operations',
      features: [
        'Unlimited records',
        'Unlimited searches',
        'Everything in Growth',
        'Up to 5 employer seats',
        { text: 'ATS integrations', badge: 'IN DEVELOPMENT' },
        'Dedicated onboarding',
      ],
      planId: 'scale',
      featured: false,
    },
  ];

  return (
    <div className="vett-page min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--vett-navy)' }}>
      {/* Skip link */}
      <a href="#main-content" className="vett-skip-link">Skip to main content</a>
      
      {/* Noise texture */}
      <div className="vett-noise" aria-hidden="true" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ 
        backgroundColor: 'rgba(14, 17, 24, 0.85)',
        borderBottom: '1px solid var(--vett-border)',
        height: 'var(--vett-nav-height)'
      }}>
        <div className="vett-container px-6">
          <div className="flex justify-between items-center h-[68px]">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ 
                background: 'var(--vett-coral-gradient)',
                boxShadow: 'var(--vett-shadow-glow)'
              }}>
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="vett-display text-xl" style={{ color: 'var(--vett-white)' }}>VETT</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/employers" className="vett-mono text-sm" style={{ 
                color: 'var(--vett-coral)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>For Employers</Link>
              <Link to="/professionals" className="vett-mono text-sm hover:opacity-100 transition-opacity" style={{ 
                color: 'var(--vett-white-dim)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>For Professionals</Link>
              <a href="#pricing" className="vett-mono text-sm hover:opacity-100 transition-opacity" style={{ 
                color: 'var(--vett-white-dim)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>Pricing</a>
            </nav>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="vett-btn-ghost rounded-md" data-testid="login-btn">
                  Sign in
                </Button>
              </Link>
              <Link to="/signup?type=employer">
                <Button className="vett-btn vett-btn-primary" data-testid="signup-btn">
                  Start Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="main-content" className="pt-32 pb-20 px-6 relative">
        {/* Glow effect */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none" style={{
          background: 'var(--vett-coral-glow)',
          filter: 'blur(100px)',
          borderRadius: '50%'
        }} aria-hidden="true" />
        
        <div className="vett-container relative z-10 text-center">
          {/* Eyebrow */}
          <div className={`vett-eyebrow mb-8 justify-center transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            For Employers
          </div>
          
          <h1 className={`vett-display text-5xl sm:text-6xl lg:text-7xl mb-8 max-w-4xl mx-auto transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ color: 'var(--vett-white)' }}>
            Hire with <em>confidence,</em>
            <br />not hope
          </h1>

          <p className={`vett-accent text-lg lg:text-xl mb-10 max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ color: 'var(--vett-white-dim)' }}>
            Search verified talent with dual-signed work records. Create your own records for employees. 
            Build a pipeline of candidates you can actually trust.
          </p>

          <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Link to="/signup?type=employer">
              <Button className="vett-btn vett-btn-primary h-14 px-8" data-testid="hero-cta-trial">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="vett-btn vett-btn-ghost h-14 px-8" data-testid="hero-cta-demo">
                Request Demo
              </Button>
            </Link>
          </div>

          <p className={`vett-mono text-xs mt-6 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ 
            color: 'var(--vett-gray-light)',
            letterSpacing: 'var(--vett-tracking-wide)'
          }}>
            14-day full access trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container">
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="vett-display text-5xl lg:text-6xl mb-2" style={{ 
                  background: 'var(--vett-coral-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>{benefit.value}</div>
                <h3 className="vett-display text-xl mb-2" style={{ color: 'var(--vett-white)' }}>{benefit.label}</h3>
                <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="vett-section">
        <div className="vett-container">
          <div className="text-center mb-16">
            <p className="vett-eyebrow justify-center mb-6">Features</p>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: 'var(--vett-white)' }}>
              Everything you need to <em>hire smarter</em>
            </h2>
            <p className="vett-accent text-lg max-w-xl mx-auto" style={{ color: 'var(--vett-white-dim)' }}>
              A complete hiring platform built around verified work records.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="vett-card p-6" data-testid={`feature-${i}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: 'var(--vett-coral-dim)'
                  }}>
                    <feature.icon className="h-6 w-6" style={{ color: 'var(--vett-coral)' }} />
                  </div>
                  <div>
                    <h3 className="vett-display text-base mb-1" style={{ color: 'var(--vett-white)' }}>{feature.title}</h3>
                    <span className="vett-pill vett-pill-coral text-[8px]">{feature.highlight}</span>
                  </div>
                </div>
                <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)', lineHeight: 'var(--vett-leading-mono)' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container max-w-4xl">
          <div className="text-center mb-16">
            <p className="vett-eyebrow justify-center mb-6">Process</p>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl" style={{ color: 'var(--vett-white)' }}>
              How employers use <em>VETT</em>
            </h2>
          </div>

          <div className="space-y-8">
            {[
              { step: '01', title: 'Start your trial', desc: 'Sign up with your business email. Get instant access to all features for 3 days.' },
              { step: '02', title: 'Search verified talent', desc: 'Browse pre-vetted candidates. Filter by tier, skills, industry. View dual-signed work records.' },
              { step: '03', title: 'Create records for your team', desc: 'Document your employees\' performance. They acknowledge, you both sign. The record becomes verified.' },
              { step: '04', title: 'Build your pipeline', desc: 'Track candidates through your hiring process. Schedule reviews. Make data-driven hiring decisions.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="vett-display text-4xl flex-shrink-0" style={{ color: 'var(--vett-coral-dim)' }}>{item.step}</div>
                <div className="pt-1">
                  <h3 className="vett-display text-lg mb-1" style={{ color: 'var(--vett-white)' }}>{item.title}</h3>
                  <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="vett-section" style={{ overflow: 'visible' }}>
        <div className="vett-container" style={{ overflow: 'visible' }}>
          <div className="text-center mb-12">
            <p className="vett-eyebrow justify-center mb-6">Pricing</p>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: 'var(--vett-white)' }}>
              Simple, transparent pricing
            </h2>
            <p className="vett-accent text-lg max-w-xl mx-auto mb-8" style={{ color: 'var(--vett-white-dim)' }}>
              14-day full access trial, no credit card required.
            </p>
            
            {/* Billing toggle */}
            <div className="inline-flex items-center gap-2 p-1.5 rounded-lg" style={{ 
              backgroundColor: 'var(--vett-surface)',
              border: '1px solid var(--vett-border)'
            }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                className="vett-mono text-xs px-4 py-2 rounded-md transition-all"
                style={{
                  background: billingCycle === 'monthly' ? 'var(--vett-coral-gradient)' : 'transparent',
                  color: billingCycle === 'monthly' ? 'var(--vett-white)' : 'var(--vett-gray-light)',
                  letterSpacing: 'var(--vett-tracking-wider)',
                  textTransform: 'uppercase'
                }}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className="vett-mono text-xs px-4 py-2 rounded-md transition-all flex items-center gap-2"
                style={{
                  background: billingCycle === 'annual' ? 'var(--vett-coral-gradient)' : 'transparent',
                  color: billingCycle === 'annual' ? 'var(--vett-white)' : 'var(--vett-gray-light)',
                  letterSpacing: 'var(--vett-tracking-wider)',
                  textTransform: 'uppercase'
                }}
                data-testid="billing-annual"
              >
                Annual
                <span className="vett-pill vett-pill-green text-[8px]">2 FREE</span>
              </button>
            </div>
          </div>

          {/* Employer Plans */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" style={{ overflow: 'visible' }}>
            {employerPlans.map((plan, i) => (
              <div 
                key={i}
                className="vett-card p-6 pt-8 relative flex flex-col"
                style={{
                  borderColor: plan.featured ? 'var(--vett-border-active)' : 'var(--vett-border)',
                  overflow: 'visible'
                }}
                data-testid={`plan-${plan.planId}`}
              >
                {plan.featured && (
                  <span 
                    className="vett-pill vett-pill-coral"
                    style={{ 
                      position: 'absolute', 
                      top: '-14px', 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      zIndex: 10
                    }}
                  >
                    POPULAR
                  </span>
                )}
                
                <h3 className="vett-display text-lg mb-2" style={{ color: 'var(--vett-white)' }}>{plan.name}</h3>
                
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="vett-display text-3xl" style={{ color: 'var(--vett-white)' }}>${plan.price.toLocaleString()}</span>
                  <span className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{plan.period}</span>
                </div>
                {plan.savings && (
                  <p className="vett-mono text-xs mb-2" style={{ color: 'var(--vett-green)' }}>{plan.savings}</p>
                )}
                
                <p className="vett-mono text-xs mb-4" style={{ color: 'var(--vett-gray-light)' }}>{plan.description}</p>
                
                <ul className="space-y-2 mb-4 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 vett-mono text-xs" style={{ color: 'var(--vett-white-dim)' }}>
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--vett-coral)' }} />
                      {typeof f === 'string' ? (
                        f
                      ) : (
                        <span className="flex items-center gap-1.5 flex-wrap">
                          {f.text}
                          <span className="vett-pill vett-pill-amber text-[8px]">{f.badge}</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                {plan.addOn && (
                  <p className="vett-mono text-xs mb-4 pt-3" style={{ 
                    color: 'var(--vett-gray-light)',
                    borderTop: '1px solid var(--vett-border)'
                  }}>{plan.addOn}</p>
                )}
                
                <Link to={`/signup?type=employer&plan=${plan.planId}`} className="mt-auto">
                  <Button className={`w-full ${plan.featured ? 'vett-btn vett-btn-primary' : 'vett-btn vett-btn-ghost'}`} data-testid={`plan-${plan.planId}-cta`}>
                    Start free trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container">
          <div className="vett-card p-8 lg:p-12 text-center" style={{
            background: 'linear-gradient(135deg, var(--vett-coral-dim), var(--vett-surface))',
            borderColor: 'var(--vett-border-active)'
          }}>
            <h2 className="vett-display text-3xl sm:text-4xl mb-4" style={{ color: 'var(--vett-white)' }}>
              Ready to transform your hiring?
            </h2>
            <p className="vett-accent text-lg mb-8 max-w-xl mx-auto" style={{ color: 'var(--vett-white-dim)' }}>
              Join forward-thinking companies who've moved beyond resumes and references.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup?type=employer">
                <Button className="vett-btn vett-btn-primary h-14 px-8" data-testid="pricing-cta-trial">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button className="vett-btn vett-btn-ghost h-14 px-8" data-testid="pricing-cta-view">
                  View All Plans
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container max-w-3xl">
          <p className="vett-eyebrow justify-center mb-6">FAQ</p>
          <h2 className="vett-display text-3xl sm:text-4xl text-center mb-12" style={{ color: 'var(--vett-white)' }}>
            Questions from <em>employers</em>
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="vett-card overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="vett-display text-sm pr-4" style={{ color: 'var(--vett-white)' }}>{faq.q}</span>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} 
                    style={{ color: 'var(--vett-gray-light)' }}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)', lineHeight: 'var(--vett-leading-mono)' }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--vett-border)' }} className="py-12 px-6">
        <div className="vett-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--vett-coral-gradient)' }}>
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <span className="vett-display text-lg" style={{ color: 'var(--vett-white)' }}>VETT</span>
              </Link>
              <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>
                Verified work history for the modern workforce.
              </p>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/employers" className="vett-mono text-xs" style={{ color: 'var(--vett-coral)' }}>For Employers</Link></li>
                <li><Link to="/professionals" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>For Professionals</Link></li>
                <li><Link to="/#pricing" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Privacy</Link></li>
                <li><Link to="/terms" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Terms</Link></li>
                <li><Link to="/impressum" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Impressum</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8" style={{ borderTop: '1px solid var(--vett-border)' }}>
            <p className="vett-mono text-xs text-center" style={{ color: 'var(--vett-gray)' }}>
              © {new Date().getFullYear()} VETT. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmployersPage;
